/*
PurchaseEditPage - Componente para editar compras existentes
Autor: Benjamín León
Fecha: 2024-12-07
*/
import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'

import usePurchaseFetcher from '../hooks/usePurchaseFetcher'
import useVehicleFetcher from '../hooks/useVehicleFetcher'
import useClientFetcher from '../hooks/useClientFetcher'

import PurchaseEdit from './PurchaseEdit'
import { Alert } from './MessageInfo'

import Purchase from '@/interfaces/Purchase'
import Vehicle from '@/interfaces/Vehicle'
import Client from '@/interfaces/Client'
import { AppPath } from '@/enums/AppPath'

const PurchaseEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { fetchPurchase, errorPurchase } = usePurchaseFetcher()
  const { fetchVehicle, errorVehicle } = useVehicleFetcher()
  const { fetchClient, errorClient } = useClientFetcher()

  const [purchase, setPurchase] = useState<Purchase | null>(null)
  const [vehicle, setVehicle] = useState<Vehicle | null>(null)
  const [client, setClient] = useState<Client | null>(null)
  const [error, setError] = useState<string>('')

  useEffect(() => {
    const fetchPurchaseData = async () => {
      if (!id) {
        setError('ID de compra no proporcionado')

        return
      }

      try {
        // Fetch purchase
        const purchaseData = await fetchPurchase(id)

        if (!purchaseData) {
          setError('No se pudo cargar la compra')

          return
        }
        setPurchase(purchaseData)

        // Fetch vehicle
        const vehicleData = await fetchVehicle(purchaseData.vehicle)

        if (!vehicleData) {
          setError('No se pudo cargar el vehículo')

          return
        }
        setVehicle(vehicleData)

        // Fetch client
        const clientData = await fetchClient(purchaseData.client)

        if (!clientData) {
          setError('No se pudo cargar el cliente')

          return
        }
        setClient(clientData)
      } catch (error) {
        console.error('Error fetching purchase data:', error)
        setError('Error al cargar los datos de la compra')
      }
    }

    fetchPurchaseData()
  }, [id])

  const handleRefresh = () => {
    // Navigate back to purchase detail after successful edit
    navigate(`/${AppPath.Purchases}/view/${id}`)
  }

  // Show errors if any
  if (error || errorPurchase || errorVehicle || errorClient) {
    return (
      <div className="w-full px-4">
        <Alert
          message={
            error || errorPurchase?.message || errorVehicle?.message || errorClient?.message || 'Error desconocido'
          }
          trigger={Date.now()}
          onTimeout={() => {}}
        />
        <button
          className="mt-4 bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
          onClick={() => navigate(-1)}
        >
          Volver
        </button>
      </div>
    )
  }

  // Show loading state
  if (!purchase || !vehicle || !client) {
    return (
      <div className="w-full px-4">
        <p>Cargando datos de la compra...</p>
      </div>
    )
  }

  // Validate if purchase is voided
  if (purchase.voidedAt) {
    return (
      <div className="w-full px-4">
        <div className="bg-red-100 p-4 rounded mb-4">
          <p className="text-red-800 font-semibold">Esta compra está anulada y no puede ser editada.</p>
        </div>
        <button
          className="mt-4 bg-gray-500 hover:bg-gray-700 text-white font-bold py-2 px-4 rounded"
          onClick={() => navigate(`/${AppPath.Purchases}/view/${id}`)}
        >
          Volver a los detalles
        </button>
      </div>
    )
  }

  return (
    <div className="w-full px-4">
      <PurchaseEdit
        vehicle={vehicle}
        client={client}
        purchase={purchase}
        onRefresh={handleRefresh}
        startInEditMode={true}
      />
    </div>
  )
}

export default PurchaseEditPage
